import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import NewAccountWizard from "../../components/Directory/NewAccountWizard";
import {
  ActionRow,
  EmptyState,
  InlineFilter,
  PageHeader,
  SkeletonTable,
  StatusPill,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import { getAccounts } from "./api";

/**
 * DirectoryAccounts — SYSTEM cross-tenant accounts list.
 *
 * Ported from clue2app-admin/frontend/src/components/OrgsTab.tsx. This
 * page is the primary bootstrap surface for new customers: the
 * "New account" CTA opens the two-step NewAccountWizard which
 * provisions an account row + mints its first ACCOUNT_ADMIN invitation
 * in a single call.
 *
 * Route gate is enforced in App.js (requiredRole SYSTEM /
 * DIRECTORY_ADMIN); ACCOUNT_ADMINs never see this tab because
 * DirectoryNavRail filters it out client-side.
 */

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function trialStatus(o) {
  if (o.billingEnabled) return { label: "Billing", color: "success" };
  const now = Date.now();
  const end = o.trialEndOn ? new Date(o.trialEndOn).getTime() : null;
  if (end === null) return { label: "No trial set", color: "default" };
  if (now > end) return { label: "Trial expired", color: "error" };
  const daysLeft = Math.max(0, Math.ceil((end - now) / 86_400_000));
  return {
    label: `Trial · ${daysLeft}d left`,
    color: daysLeft <= 7 ? "warning" : "default",
  };
}

const COLUMN_SPEC = [
  { label: "Name" },
  { label: "Trial ends" },
  { label: "Projects", align: "right", width: 96 },
  { label: "Beta", width: 80 },
  { label: "Status", width: 140 },
  { label: "Account ID", mono: true },
  { label: "", align: "right", width: 48 },
];

export default function DirectoryAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    getAccounts()
      .then((data) => setAccounts(data))
      .catch((e) => {
        setErr(
          e.status === 403
            ? "SYSTEM privileges required to view all accounts."
            : `Failed to load accounts: ${e.message}`
        );
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreated = (result) => {
    const name =
      result?.invitation?.accountName || result?.accountName || "account";
    setFlash(`Account "${name}" created. Refreshing…`);
    load();
    setTimeout(() => setFlash(null), 5000);
  };

  const filtered =
    accounts?.filter(
      (o) =>
        !q ||
        (o.name ?? "").toLowerCase().includes(q.toLowerCase()) ||
        (o.id ?? "").toLowerCase().includes(q.toLowerCase())
    ) ?? [];

  const newAccountButton = (
    <Tooltip
      title="Create a new account (tenant). You'll invite the first admin as part of this flow."
      arrow
      enterDelay={400}
    >
      <span>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setWizardOpen(true)}
          sx={{ textTransform: "none" }}
          aria-label="New account"
        >
          New account
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <PageHeader
        title="Accounts"
        titleInfo="Every customer on the platform is one account. An account holds projects, users, groups, and roles."
        subtitle="Cross-tenant list of accounts on the platform. SYSTEM only."
        action={newAccountButton}
      />

      {flash && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFlash(null)}>
          {flash}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {!accounts && !err && <SkeletonTable columns={COLUMN_SPEC} rows={6} />}

      {accounts && (
        <>
          <InlineFilter
            value={q}
            onChange={setQ}
            placeholder="Filter by name or id"
            count={{
              shown: filtered.length,
              total: accounts.length,
              noun: "account",
            }}
          />

          {filtered.length === 0 ? (
            <EmptyState
              icon={<BusinessOutlinedIcon />}
              title={q ? "No accounts match" : "No accounts yet"}
              description={
                q
                  ? "Try clearing the filter or searching for a different name."
                  : "Bootstrap the first customer account and mint its ACCOUNT_ADMIN invitation."
              }
              variant={q ? "no-match" : "empty"}
              primaryAction={q ? undefined : newAccountButton}
            />
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: dirTokens.radius }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_SPEC.map((c) => (
                      <TableCell
                        key={c.label}
                        align={c.align || "left"}
                        sx={c.mono ? { fontFamily: "monospace" } : undefined}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((o) => {
                    const s = trialStatus(o);
                    const projects =
                      typeof o.projectCount === "number"
                        ? o.projectCount
                        : Array.isArray(o.projects)
                          ? o.projects.length
                          : null;
                    return (
                      <ActionRow
                        key={o.id}
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/directory/accounts/${o.id}`)}
                        role="link"
                        aria-label={`Open account ${o.name || o.id}`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/directory/accounts/${o.id}`);
                          }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={500}>
                              {o.name || "—"}
                            </Typography>
                            {o.enabled === false && (
                              <StatusPill status="disabled" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{formatDate(o.trialEndOn)}</TableCell>
                        <TableCell align="right">
                          <span
                            style={{
                              fontVariantNumeric: "tabular-nums",
                              fontSize: 13,
                            }}
                          >
                            {projects ?? "—"}
                            {typeof o.maxProjects === "number" && projects !== null
                              ? ` / ${o.maxProjects}`
                              : null}
                          </span>
                        </TableCell>
                        <TableCell>
                          {o.beta ? (
                            <Tooltip
                              title="Beta accounts skip billing setup and get the beta feature-flag set."
                              arrow
                              enterDelay={400}
                            >
                              <Chip
                                label="Beta"
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ fontSize: 12 }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.label}
                            size="small"
                            color={s.color}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                          }}
                        >
                          {o.id}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 48 }}>
                          <ChevronRightIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </TableCell>
                      </ActionRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <NewAccountWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={handleCreated}
      />
    </motion.div>
  );
}
